'use client';

import { LDProvider } from 'launchdarkly-react-client-sdk';
import { type PropsWithChildren } from 'react';

import { flagsContext } from '@/contexts/flagsContext';

import { useEnvironment } from './useEnvironment';
import { useUser } from './useUser';

export const LaunchDarklyProvider = (
  props: PropsWithChildren<{ clientId: string | undefined }>
) => {
  const { isDev, isDemo } = useEnvironment();
  const { currentProjectId, user } = useUser();
  if (!currentProjectId) {
    return props.children;
  }

  if (isDemo || isDev) {
    return (
      <flagsContext.Provider
        value={{
          flags: {
            // Amir
            NonCognitoEonAccounts: false,
            SkipOnboarding: false,
            dlsgFrontend: false,
            assistantEnabled: false,
            LlgFrontend: false,
            ActionApprovals: true,
            // LakehouseBaseUrl: 'http://localhost:3003',

            // Erez
            JobsStepsEnabled: true,
            enableHourlyBackup: false, // opened only for Fanatics
            AzureSqlVirtualMachineSupport: true, // permanent
            ownCustomerOnboardingEnabled: false,
            byovEnabled: true,
            hourlyDlsgPolicyEnabled: false,
            OnboardTenantAgainEnabled: true,
            GcpRestoreOrgAndFolderSupport: true,
            DeleteVaultEnabled: true,
            SecurityFindings: true,

            // Yaara
            EnableGDSGPage: false,

            // Moshe
            SapHanaPitrSupport: true,
            MongoAtlasSupport: true,

            // Ilya
            ExploreFilesForDatabaseVms: true,

            // Keren
            RdsRestoreWithoutEncryptionEnabled: false,
            RecordRestoreResultsEnabled: true,

            // Dvir
            GcpVmHighFrequencyBackupEnabled: true,
            BypassSourceAccountRequirement: false,

            // Abir
            RdsHighFrequencyBackupEnabled: true,

            // Omer
            RoleBackupPoliciesConfig: true,

            // Eldar
            EnableAzureVaultNetworkIsolation: true,

            // Ariel
            AzureSnapshotsDiscoverySupport: true,

            // Ran
            AwsSelectiveOnboarding: true,
            SkipOrgUnitInvokeDiscovery: true,
            AzureFilesSupport: true,
            AwsRdsNativeBackupSupport: true,
            AzureSqlDatabaseNativePitrPolicyEnabled: true,

            // Tzachi
            AzureMultipleAppRegistrations: false,
            AzureCustomPermissionsModel: false,

            // Shirly
            IsDatadogNotificationEnabled: true,

            // Google Workspace
            GoogleWorkspaceSupport: true,
            GwsInPlaceRestoreOnly: false,

            // Liran - Aurora Global DB
            AuroraGlobalDbDiscoverySupport: true,

            // Eden
            EnableMrapForByov: true,

            // Oryan
            BigQueryNativeRestoreEnabled: true,
            EnableBigQueryNativePolicy: true,
            GcpProjectLabelTagFilterEnabled: true,
            m365BackupConsole: true,

            // Benji
            SensitivityAnnotations: true,

            // Liran - RDS Restore
            RdsRestorePreserveOriginalMemberNames: false,
            AwsKeyspacesSupport: true,

            // MatanR
            RdsCdcUiEnabled: true,

            // Or
            AiAgents: false,

            // Yair
            ZeroEtlUiEnabled: true,
            LakehouseEnabled: true,
            lakehouseZeroEtlUiEnabled: true,

            // Daniel - Export access-denied items
            EnableExportAccessDeniedItems: true,
            InventoryIssuesColumn: false,

            // Lior - Restore Templates
            RestoreTemplates: isDev,

            // Jonathan - Inventory entities filter
            EnableInventoryEntitiesFilter: true,
          },
          flagKeyMap: {},
          ldClient: undefined,
        }}
      >
        {props.children}
      </flagsContext.Provider>
    );
  }

  if (!props.clientId || !user) {
    return props.children;
  }

  return (
    <LDProvider
      reactOptions={{
        reactContext: flagsContext,
        useCamelCaseFlagKeys: false,
      }}
      clientSideID={props.clientId}
      context={{
        kind: 'multi',
        user: {
          key: currentProjectId, // backward compatibility
          // name: user.givenName,
          // email: user.email,
          // id: user.id,
          name: user.eonAccount.name,
          domain: user.eonAccount.domain,
        },
        organization: {
          key: user.eonAccount.id,
          name: user.eonAccount.name,
          domain: user.eonAccount.domain,
          projectId: currentProjectId,
        },
      }}
      options={
        {
          /* ... */
        }
      }
    >
      {props.children}
    </LDProvider>
  );
};

// Pre-login provider for the public route group. There is no authenticated
// user (and no UserProvider) here, so flags are evaluated against an anonymous
// context — LaunchDarkly generates and persists a stable key, giving consistent
// percentage-rollout bucketing per browser. On login, the authenticated
// LaunchDarklyProvider re-evaluates against the real user/org context.
export const PublicLaunchDarklyProvider = (
  props: PropsWithChildren<{ clientId: string | undefined }>
) => {
  const { isDev, isDemo, isTest } = useEnvironment();

  // Pre-login pages don't need real flag values in dev/demo/test — useFeatureFlags
  // resolves to defaults (and the ?flags= override still applies), and
  // useLaunchDarkly() already reports ready in these envs so nothing waits on a
  // client that would never initialize.
  if (isDemo || isDev || isTest || !props.clientId) {
    return props.children;
  }

  return (
    <LDProvider
      reactOptions={{
        reactContext: flagsContext,
        useCamelCaseFlagKeys: false,
      }}
      clientSideID={props.clientId}
      context={{ kind: 'user', anonymous: true }}
      options={
        {
          /* ... */
        }
      }
    >
      {props.children}
    </LDProvider>
  );
};
